import { IconDownload, IconExternalLink } from '@tabler/icons-react';
import {
  ApplicationSubmissionMethod,
  CarrierPaymentDto,
  CreateApplicationResultDto,
  EmployeePaymentMethodType,
  PaymentsProvider,
  isDefined,
} from '@zorro/clients';
import { APP_ROUTES, getFullName } from '@zorro/common-utils';
import { Alert } from '@zorro/design-system/v2/Alert';
import { Button } from '@zorro/design-system/v2/Button';
import { Divider } from '@zorro/design-system/v2/Divider';
import { DocumentCard } from '@zorro/design-system/v2/DocumentCard';
import { Loader } from '@zorro/design-system/v2/Loader';
import { RadioCard, RadioGroup } from '@zorro/design-system/v2/Radio';
import {
  Center,
  FormErrorMessage,
  Grid,
  Group,
  Stack,
  Text,
} from '@zorro/web/design-system';
import {
  DisplayPlanMarket,
  PaymentStatusSection,
  PlanMarketConfig,
  derivePaymentMethodTypeFromBenefit,
  planMarketEnrollmentValues,
  useLynxPaymentsContext,
} from '@zorro/web/shared-ui';
import {
  isIchraLetterVisible,
  screamingSnakeCaseToText,
  useEmployerFeatureFlag,
  useMonolithQuery,
  useRoles,
} from '@zorro/web/ui-utils';
import { Controller, useWatch } from 'react-hook-form';

import { EmployeePageTabType } from '../../EmployeePage/EmployeePageContent';
import {
  CarrierApplicationModalStepType,
  CarrierApplicationStep,
} from '../CarrierApplicationModal';
import { CarrierApplicationCommunicationSection } from './sections/CarrierApplicationCommunicationSection';
import { CarrierApplicationEvidenceSection } from './sections/CarrierApplicationEvidenceSection';
import { CarrierSubmissionMethodSection } from './sections/CarrierSubmissionMethodSection';
import { useBrokerAttestation } from './useBrokerAttestation';
import { CarrierApplicationFormState } from './useCarrierApplicationForm';
import { useCarrierDocumentDownload } from './useCarrierDocumentDownload';
import { useInitialAllocationGate } from './useInitialAllocationGate';

const isAllocationGateEnabled = (
  paymentsProvider: PaymentsProvider | undefined,
  majorMedicalPlan:
    Parameters<typeof derivePaymentMethodTypeFromBenefit>[0] | undefined
): boolean => {
  if (paymentsProvider !== PaymentsProvider.LYNX) return false;
  const paymentMethodType =
    majorMedicalPlan && derivePaymentMethodTypeFromBenefit(majorMedicalPlan);
  return paymentMethodType !== EmployeePaymentMethodType.SELF_PAY;
};

type Props = {
  employerId: string;
  employeeId: string;
  onboardingPeriodId: string;
  benefitId?: string;
  onClose: () => void;
  onDetailsReviewOpen: () => void;
  carrierApplicationForm: CarrierApplicationFormState;
  tab: EmployeePageTabType;
};

const CarrierApplicationCompleteStepInner = ({
  employerId,
  employeeId,
  onboardingPeriodId,
  benefitId,
  onClose,
  onDetailsReviewOpen,
  carrierApplicationForm,
  tab,
}: Props) => {
  const { isOmnipotentAdmin } = useRoles();
  const { paymentsProvider } = useLynxPaymentsContext({
    employerId,
    employeeId,
  });

  const {
    paymentStatusForm,
    submissionForm,
    shouldHideCommunicationSection,
    requiredActions,
    isEnrollConnectSubmitted,
    enrollConnectResult,
    submittedApplication,
    setEnrollConnectResult,
    addSubmissionEvidence,
    persistProofOfConsentDocuments,
    persistSpecialEnrollmentDocuments,
    onSubmit,
  } = carrierApplicationForm;

  const { data: onboardingPeriod } = useMonolithQuery({
    method: 'onboardingPeriodsControllerFindOne',
    params: [employeeId, onboardingPeriodId],
  });
  const { data: ichraTemplate, isLoading: isIchraTemplateLoading } =
    useMonolithQuery({
      method: 'benefitDocumentsControllerGetIchraLetterTemplate',
      params: [],
    });
  const { data: benefits } = useMonolithQuery({
    method: 'benefitsControllerGetBenefits',
    params: [onboardingPeriodId],
  });
  const { data: employee } = useMonolithQuery({
    method: 'employeesControllerFindOne',
    params: [employeeId],
  });
  // The payment redirect only becomes valid once the application is submitted.
  const { data: carrierPayment } = useMonolithQuery({
    method: 'applicationsControllerGetPayment',
    params: isDefined(submittedApplication) && benefitId && [benefitId],
  });

  const allocationGate = useInitialAllocationGate({
    employeeId,
    benefitId,
    employeeName: employee ? getFullName(employee) : 'The member',
    enabled: isAllocationGateEnabled(
      paymentsProvider,
      benefits?.majorMedicalBenefit?.plan
    ),
  });

  const isMajorMedicalPlan =
    isDefined(benefitId) && benefits?.majorMedicalBenefit?.id === benefitId;
  const withIchraLetter = isIchraLetterVisible(onboardingPeriod?.effectiveFrom);

  const { control, formState } = submissionForm;
  const { errors, isValid: isSubmissionFormValid } = formState;
  const { formState: paymentStatusFormState } = paymentStatusForm;
  const { isValid: isPaymentStatusFormValid } = paymentStatusFormState;

  const submissionMethod = useWatch({ control, name: 'submissionMethod' });

  const brokerAttestationGate = useBrokerAttestation({
    employeeId,
    employerId,
    onboardingPeriodId,
    benefitId,
    submissionForm,
  });

  const { isDownloadingIchra, onDownloadIchraLetter } =
    useCarrierDocumentDownload({
      employeeId,
      onboardingPeriodId,
      ichraTemplateEid: ichraTemplate?.eid,
      ichraTemplateTitle: ichraTemplate?.title,
      effectiveFrom: onboardingPeriod?.effectiveFrom,
    });

  const shouldShowHealthSherpa = isOmnipotentAdmin && isMajorMedicalPlan;
  const { isOn: isEnrollConnectEnabled } = useEmployerFeatureFlag(
    'enroll_connect_enabled',
    employerId
  );
  const shouldShowEnrollConnect =
    isEnrollConnectEnabled && shouldShowHealthSherpa;

  const shouldShowApplicationSections =
    submissionMethod === ApplicationSubmissionMethod.ENROLL_CONNECT
      ? isEnrollConnectSubmitted
      : isDefined(submissionMethod);

  const handleEnrollConnectResult = (result: CreateApplicationResultDto) => {
    setEnrollConnectResult(result);
    if (result.status !== CreateApplicationResultDto.status.CREATED) return;

    submissionForm.setValue('planMarket', DisplayPlanMarket.OFF_EXCHANGE, {
      shouldValidate: true,
      shouldDirty: true,
    });
    if (result.submissionEvidence) {
      addSubmissionEvidence(result.submissionEvidence);
    }
  };

  const handleStepSubmit = async () => {
    const success = await onSubmit();
    if (success) onClose();
    return { navigate: false };
  };

  const isSubmitEnabled =
    isSubmissionFormValid &&
    isPaymentStatusFormValid &&
    !allocationGate.isSubmitLocked &&
    !brokerAttestationGate.isSubmitLocked;

  const isPaymentRedirect =
    carrierPayment?.channel === CarrierPaymentDto.channel.REDIRECT;
  const paymentPhoneNumber =
    carrierPayment?.channel === CarrierPaymentDto.channel.PHONE
      ? carrierPayment.phoneNumber
      : undefined;

  return (
    <CarrierApplicationStep
      benefitId={benefitId}
      step={CarrierApplicationModalStepType.COMPLETE_APPLICATION}
      employerId={employerId}
      employeeId={employeeId}
      onboardingPeriodId={onboardingPeriodId}
      onSubmit={handleStepSubmit}
      submitLabel="Confirm application submission"
      isSubmitDisabled={!isSubmitEnabled}
      withReviewCard
      tab={tab}
    >
      {withIchraLetter && (
        <>
          <Stack mb="xl" gap="xxs">
            <Group justify="space-between" align="center" wrap="nowrap">
              <Stack gap="xxs">
                <Text size="md" fw={600}>
                  ICHRA/CHOICE letter
                </Text>
                <Text
                  size="sm"
                  fw={400}
                  c="var(--color-text-default-secondary)"
                >
                  If required for the application, download the auto-filled
                  ICHRA/CHOICE letter
                </Text>
              </Stack>
              {(ichraTemplate?.eid || isIchraTemplateLoading) && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={onDownloadIchraLetter}
                  loading={isDownloadingIchra}
                  disabled={isIchraTemplateLoading}
                  leftSection={<IconDownload size={16} />}
                >
                  Download ICHRA/CHOICE letter
                </Button>
              )}
            </Group>
            {!ichraTemplate?.eid && !isIchraTemplateLoading && (
              <Alert variant="error">
                ICHRA/CHOICE Letter template not found. Please contact support.
              </Alert>
            )}
          </Stack>
          <Divider />
        </>
      )}
      <Stack gap="xl">
        <CarrierSubmissionMethodSection
          employeeId={employeeId}
          employerId={employerId}
          onboardingPeriodId={onboardingPeriodId}
          benefitId={benefitId}
          shouldShowPlanGeneratePdf={isMajorMedicalPlan}
          shouldShowHealthSherpa={shouldShowHealthSherpa}
          shouldShowEnrollConnect={shouldShowEnrollConnect}
          onDetailsReviewOpen={onDetailsReviewOpen}
          enrollConnectResult={enrollConnectResult}
          submittedApplication={submittedApplication}
          onEnrollConnectResult={handleEnrollConnectResult}
          persistProofOfConsentDocuments={persistProofOfConsentDocuments}
          persistSpecialEnrollmentDocuments={persistSpecialEnrollmentDocuments}
          submissionForm={submissionForm}
          brokerAttestationGate={brokerAttestationGate}
        />
        {shouldShowApplicationSections && (
          <>
            <Divider />
            <Stack my="xl" gap={isEnrollConnectSubmitted ? 'lg' : 'xxs'}>
              <Text w="100%" size="md" fw={600}>
                Plan market
              </Text>
              <Stack gap="md">
                {isEnrollConnectSubmitted ? (
                  <DocumentCard
                    fileName="Off exchange (this application method is only for off-exchange plans)"
                    variant="shadow"
                  />
                ) : (
                  <>
                    <Text
                      w="100%"
                      size="sm"
                      c="var(--color-text-default-secondary)"
                    >
                      Select the market in which this policy is being submitted
                    </Text>
                    <Controller
                      name="planMarket"
                      control={control}
                      render={({ field: { value, onChange, ...rest } }) => (
                        <RadioGroup
                          {...rest}
                          value={String(value)}
                          required
                          onChange={onChange}
                        >
                          <Grid gap="md">
                            {planMarketEnrollmentValues.map((radioValue) => {
                              const { color, icon: IconComponent } =
                                PlanMarketConfig[radioValue];
                              const label =
                                screamingSnakeCaseToText(radioValue);

                              return (
                                <Grid.Col span={4} key={radioValue}>
                                  <RadioCard
                                    size="md"
                                    icon={<IconComponent />}
                                    iconColor={color}
                                    value={radioValue}
                                    checked={radioValue === value}
                                    label={label}
                                  />
                                </Grid.Col>
                              );
                            })}
                          </Grid>
                        </RadioGroup>
                      )}
                    />
                    <FormErrorMessage fieldName="planMarket" errors={errors} />
                  </>
                )}
              </Stack>
            </Stack>

            <Divider />
            <CarrierApplicationEvidenceSection
              onboardingPeriodId={onboardingPeriodId}
              isEnrollConnectSubmitted={isEnrollConnectSubmitted}
              submissionForm={submissionForm}
            />

            <Divider />
            <PaymentStatusSection
              paymentStatusForm={paymentStatusForm}
              onboardingPeriodId={onboardingPeriodId}
              headerAction={
                isPaymentRedirect && benefitId ? (
                  <Button
                    size="sm"
                    rightSection={<IconExternalLink size={16} />}
                    onClick={() =>
                      window.open(
                        APP_ROUTES.CARRIER_PAYMENT(benefitId),
                        '_blank'
                      )
                    }
                  >
                    Go to carrier payment
                  </Button>
                ) : (
                  paymentPhoneNumber && (
                    <Text size="sm">
                      Set up the initial premium payment by phone:{' '}
                      {paymentPhoneNumber}
                    </Text>
                  )
                )
              }
            />
            {isMajorMedicalPlan && !shouldHideCommunicationSection && (
              <CarrierApplicationCommunicationSection
                onboardingPeriodId={onboardingPeriodId}
                requiredActions={requiredActions}
                submissionForm={submissionForm}
              />
            )}
          </>
        )}
        {allocationGate.alert && (
          <Alert variant={allocationGate.alert.variant}>
            {allocationGate.alert.message}
          </Alert>
        )}
      </Stack>
    </CarrierApplicationStep>
  );
};

export const CarrierApplicationCompleteStep = (props: Props) => {
  const { employerId, employeeId, onboardingPeriodId } = props;

  const { paymentsProvider } = useLynxPaymentsContext({
    employerId,
    employeeId,
  });

  const { data: employer, isLoading } = useMonolithQuery({
    method: 'employersControllerFindOne',
    params: [employerId],
  });
  const { data: onboardingPeriod } = useMonolithQuery({
    method: 'onboardingPeriodsControllerFindOne',
    params: [employeeId, onboardingPeriodId],
  });
  const { data: benefits } = useMonolithQuery({
    method: 'benefitsControllerGetBenefits',
    params: [onboardingPeriodId],
  });

  // Gate on data presence only - gating on isFetching would swap the step for
  // the loader on every benefits refetch, unmounting it and wiping form state.
  if (
    !paymentsProvider ||
    isLoading ||
    !employer ||
    !onboardingPeriod ||
    !benefits
  ) {
    return (
      <Center mih="calc(100vh - 18.5rem)">
        <Loader />
      </Center>
    );
  }

  return <CarrierApplicationCompleteStepInner {...props} />;
};
